export const LEADERBOARD_TYPES = {
	PNL: 'pnl',
	FEES: 'fees',
} as const;

export type LeaderboardType = (typeof LEADERBOARD_TYPES)[keyof typeof LEADERBOARD_TYPES];

export const TIMEFRAMES = {
	DAILY: 'daily',
	WEEKLY: 'weekly',
	MONTHLY: 'monthly',
	TOP: 'top',
} as const;

export type LeaderboardTimeframe = (typeof TIMEFRAMES)[keyof typeof TIMEFRAMES];

export interface TimeframeOption {
	value: LeaderboardTimeframe;
	label: string;
}

export const TIMEFRAME_OPTIONS: readonly TimeframeOption[] = Object.freeze([
	{ value: TIMEFRAMES.DAILY, label: 'Daily' },
	{ value: TIMEFRAMES.WEEKLY, label: 'Weekly' },
	{ value: TIMEFRAMES.MONTHLY, label: 'Monthly' },
	{ value: TIMEFRAMES.TOP, label: 'Top 10' },
]);

export const DEFAULT_TIMEFRAME: LeaderboardTimeframe = TIMEFRAMES.DAILY;

export const isTimeframe = (value: unknown): value is LeaderboardTimeframe =>
	TIMEFRAME_OPTIONS.some((option) => option.value === value);

export interface RankedRowBase {
	public_key: string;
	rank: number;
	trade_count: number;
}

export interface PnlLeaderboardRow extends RankedRowBase {
	type: typeof LEADERBOARD_TYPES.PNL;
	pnl: number;
}

export interface FeesLeaderboardRow extends RankedRowBase {
	type: typeof LEADERBOARD_TYPES.FEES;
	total_fees: number;
	total_volume: number;
}

export interface LeaderboardRowByType {
	[LEADERBOARD_TYPES.PNL]: PnlLeaderboardRow;
	[LEADERBOARD_TYPES.FEES]: FeesLeaderboardRow;
}

export type LeaderboardRowFor<TType extends LeaderboardType> = LeaderboardRowByType[TType];
export type LeaderboardRow = LeaderboardRowFor<LeaderboardType>;
export type RankedRow = LeaderboardRow;

export interface LeaderboardPageResult<TRow extends LeaderboardRow = LeaderboardRow> {
	rows: readonly TRow[];
	total: number;
}

export interface PnlLeaderboardSource {
	type: typeof LEADERBOARD_TYPES.PNL;
	parseResponse: (response: unknown) => LeaderboardPageResult<PnlLeaderboardRow>;
}

export interface FeesLeaderboardSource {
	type: typeof LEADERBOARD_TYPES.FEES;
	parseResponse: (response: unknown) => LeaderboardPageResult<FeesLeaderboardRow>;
}

export type LeaderboardSource = PnlLeaderboardSource | FeesLeaderboardSource;

// `timestamp` covers the board as a whole: a later block does not renew it.
export interface BoardCache {
	blocks: ReadonlyMap<number, readonly LeaderboardRow[]>;
	total: number;
	timestamp: number;
}

export const toTimeframe = (value: unknown): LeaderboardTimeframe =>
	isTimeframe(value) ? value : DEFAULT_TIMEFRAME;
